/**
 * App Registry Service - API Client for User Apps
 *
 * Manages persistent user-created apps via orbit-gateway proxy:
 * - Fetch user's apps
 * - Launch apps
 * - Update app positions (for drag-to-reorder)
 * - Delete apps
 */
import { getIdToken } from './firebase';

// Use orbit-gateway as proxy to internal app-registry service
const ORBIT_GATEWAY_URL = import.meta.env.VITE_ORBIT_GATEWAY_URL || 'https://orbit-gateway-buzbhnk2ha-uc.a.run.app';
const APPS_API_BASE = `${ORBIT_GATEWAY_URL}/api/apps`;

// =============================================================================
// TYPES
// =============================================================================

export interface UserApp {
  id: string;
  app_id: string;
  user_id: string;
  name: string;
  icon: string;
  description?: string;
  url?: string;
  position: number;
  last_used?: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    notification_count?: number;
    status?: 'active' | 'inactive' | 'error' | 'published';
    tags?: string[];
    instructions?: string;
    goal?: string;
    knowledge?: any[];
  };
}

export interface CreateAppRequest {
  name: string;
  icon: string;
  description?: string;
  url?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateAppRequest {
  name?: string;
  icon?: string;
  description?: string;
  url?: string;
  position?: number;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get authenticated headers with Firebase token
 */
async function getHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

// =============================================================================
// API METHODS
// =============================================================================

/**
 * Get all apps for the authenticated user
 * Route: GET /api/apps/apps/{userId} -> app-registry GET /apps/{user_id}
 */
export async function getUserApps(userId: string): Promise<UserApp[]> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/apps/${userId}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      if (response.status === 404) {
        // User has no apps yet
        return [];
      }
      throw new Error(`Failed to fetch apps: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.apps || [];
  } catch (error) {
    console.error('[AppRegistry] Error fetching apps:', error);
    throw error;
  }
}

/**
 * Get a single app by ID
 * Route: GET /api/apps/get/{appId} -> app-registry GET /get/{app_id}
 */
export async function getApp(appId: string): Promise<UserApp> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/get/${appId}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch app: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.app;
  } catch (error) {
    console.error('[AppRegistry] Error fetching app:', error);
    throw error;
  }
}

/**
 * Create a new app for the user
 * Route: POST /api/apps/register -> app-registry POST /register
 */
export async function createApp(userId: string, app: CreateAppRequest): Promise<UserApp & { app_id?: string }> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/register`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        user_id: userId,
        ...app
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create app: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    // Register endpoint returns { status, app_id, manifest }
    return { ...data.manifest, app_id: data.app_id };
  } catch (error) {
    console.error('[AppRegistry] Error creating app:', error);
    throw error;
  }
}

/**
 * Update an existing app
 * Route: PUT /api/apps/update/{appId} -> app-registry PUT /update/{app_id}
 */
export async function updateApp(appId: string, updates: UpdateAppRequest): Promise<UserApp> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/update/${appId}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update app: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.app;
  } catch (error) {
    console.error('[AppRegistry] Error updating app:', error);
    throw error;
  }
}

/**
 * Update app position (for drag-to-reorder)
 */
export async function updateAppPosition(appId: string, position: number): Promise<UserApp> {
  return updateApp(appId, { position });
}

/**
 * Update last used timestamp (when app is launched)
 * Route: POST /api/apps/launch/{appId} -> app-registry POST /launch/{app_id}
 */
export async function updateLastUsed(appId: string): Promise<UserApp> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/launch/${appId}`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to update last used: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.app;
  } catch (error) {
    console.error('[AppRegistry] Error updating last used:', error);
    throw error;
  }
}

/**
 * Delete an app
 * Route: DELETE /api/apps/delete/{appId} -> app-registry DELETE /delete/{app_id}
 */
export async function deleteApp(appId: string): Promise<void> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/delete/${appId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to delete app: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('[AppRegistry] Error deleting app:', error);
    throw error;
  }
}

/**
 * Get launch configuration for an app (includes preview_html for rendering)
 * Route: GET /api/apps/launch/{appId} -> app-registry GET /launch/{app_id}
 */
export interface LaunchConfig {
  app_id: string;
  name: string;
  description?: string;
  runtime: string;
  entry_point: string;
  preview_html: string;
  artifacts: Array<{ filename: string; url: string; path: string; size: number }>;
  version: string;
}

export async function getLaunchConfig(appId: string): Promise<LaunchConfig> {
  try {
    const headers = await getHeaders();

    const response = await fetch(`${APPS_API_BASE}/launch/${appId}`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to get launch config: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.launch_config;
  } catch (error) {
    console.error('[AppRegistry] Error getting launch config:', error);
    throw error;
  }
}

/**
 * Launch an app (opens URL and updates last_used)
 */
export async function launchApp(app: UserApp): Promise<void> {
  try {
    // Update last used timestamp
    await updateLastUsed(app.id);

    // Open app URL if available
    if (app.url) {
      window.open(app.url, '_blank', 'noopener,noreferrer');
    } else {
      console.warn(`[AppRegistry] App ${app.id} has no URL to launch`);
    }
  } catch (error) {
    console.error('[AppRegistry] Error launching app:', error);
    throw error;
  }
}

/**
 * Reorder apps (batch update positions)
 * Note: app-registry may not support batch reorder - falls back to individual updates
 */
export async function reorderApps(apps: Array<{ id: string; position: number }>): Promise<void> {
  try {
    // Update each app's position individually
    await Promise.all(
      apps.map(({ id, position }) => updateAppPosition(id, position))
    );
  } catch (error) {
    console.error('[AppRegistry] Error reordering apps:', error);
    throw error;
  }
}

/**
 * Save preview HTML to an app (called after god-developer build completes)
 */
export async function saveAppPreviewHtml(appId: string, previewHtml: string): Promise<void> {
  try {
    await updateApp(appId, { preview_html: previewHtml } as unknown as UpdateAppRequest);
  } catch (error) {
    console.error('[AppRegistry] Error saving preview HTML:', error);
    // Non-critical — don't throw, the app is still registered
  }
}

// Alias for backwards compatibility
export { getUserApps as getApps };
